/**
 * Códigos de barras.
 *
 * A barcode is a number and nothing else: not the name, not the size, not the price. What it
 * is worth here is that the same number comes back every time the same can is scanned, so the
 * shop can say once which product it belongs to and never type that product's name again.
 *
 * **The same product has more than one spelling.** A retail barcode is a GTIN, and GS1 prints
 * them at different lengths: a US can of Pringles carries a 12-digit UPC-A, and the very same
 * number with a zero in front is its 13-digit EAN-13. A phone camera reads the 12; some USB
 * scanners send the 13; somebody copying the digits under the bars types whichever they see.
 * GS1's own rule is to compare GTINs as 14 digits, zero-padded on the left, and that is what
 * `clavesCodigo` does — so all three find the same product.
 *
 * UPC-E is the other one: a UPC-A with its zeros squeezed out to fit a pack of gum, eight digits
 * on the wrapper standing for twelve. It is expanded to the UPC-A it abbreviates.
 *
 * **The last digit is a check digit**, which is how a misread is told apart from a real code.
 * A number of GTIN length whose check digit does not add up is not treated as a GTIN at all: it
 * is kept exactly as written, like a Code 128 label the shop printed itself or a supplier's
 * internal code, and only ever matches itself.
 */

/**
 * What somebody typed or a scanner sent, ready to compare and to store.
 *
 * Digits printed under the bars come in groups — «7 441029 512345» — and a person copying them
 * keeps the gaps. Spaces and dashes between digits are dropped; in anything that is not purely
 * digits they are part of the code and stay.
 */
export function limpiarCodigo(texto: string): string {
  const recortado = texto.trim();
  const digitos = recortado.replace(/[\s-]/g, '');
  return /^\d+$/.test(digitos) ? digitos : recortado;
}

/** GS1 check digit for the digits before it: weights 3, 1, 3, 1… counted from the right. */
export function digitoControl(datos: string): number {
  let suma = 0;
  for (let i = 0; i < datos.length; i++) {
    const d = datos.charCodeAt(datos.length - 1 - i) - 48;
    suma += i % 2 === 0 ? d * 3 : d;
  }
  return (10 - (suma % 10)) % 10;
}

/** An EAN-8, UPC-A, EAN-13 or GTIN-14 whose check digit adds up. */
export function esGtin(codigo: string): boolean {
  if (!/^(\d{8}|\d{12,14})$/.test(codigo)) return false;
  return digitoControl(codigo.slice(0, -1)) === codigo.charCodeAt(codigo.length - 1) - 48;
}

/**
 * The UPC-A that a UPC-E abbreviates, or `null` when these eight digits are not one.
 *
 * The sixth digit says where the zeros were taken from. The check digit is the UPC-A's, so it
 * has to add up after expanding — which is also what tells a UPC-E from an EAN-8 that happens
 * to start with 0 or 1.
 */
export function expandirUpcE(codigo: string): string | null {
  if (!/^[01]\d{7}$/.test(codigo)) return null;
  const [sistema, d1, d2, d3, d4, d5, d6, control] = codigo;
  let medio: string;
  switch (d6) {
    case '0':
    case '1':
    case '2':
      medio = `${d1}${d2}${d6}0000${d3}${d4}${d5}`;
      break;
    case '3':
      medio = `${d1}${d2}${d3}00000${d4}${d5}`;
      break;
    case '4':
      medio = `${d1}${d2}${d3}${d4}00000${d5}`;
      break;
    default:
      medio = `${d1}${d2}${d3}${d4}${d5}0000${d6}`;
  }
  const upcA = `${sistema}${medio}${control}`;
  return esGtin(upcA) ? upcA : null;
}

/**
 * Every spelling under which this code names the same thing.
 *
 * Two codes are the same code when they share a key. A GTIN's key is its 14-digit form, and a
 * UPC-E also answers to its expansion; anything else is only ever itself. The prefixes keep
 * the two kinds apart, so a label that merely looks like a padded GTIN can never meet one.
 */
export function clavesCodigo(codigo: string): string[] {
  const limpio = limpiarCodigo(codigo);
  if (!limpio) return [];
  const claves = [esGtin(limpio) ? `gtin:${limpio.padStart(14, '0')}` : `texto:${limpio}`];
  const upcA = limpio.length === 8 ? expandirUpcE(limpio) : null;
  if (upcA) claves.push(`gtin:${upcA.padStart(14, '0')}`);
  return claves;
}

export function mismoCodigo(a: string, b: string): boolean {
  const deA = clavesCodigo(a);
  return clavesCodigo(b).some((k) => deA.includes(k));
}

/** The longest code a product can carry; the event schema refuses anything longer. */
export const CODIGO_MAX = 40;
